import {
  Conditions,
  Item,
  ItemRestriction,
  OPERATORS,
  Restrictions,
  type RnbCalculatorPayload,
} from 'typings'
import { toAPIOriginalValues, toAPIPromotionBase } from '..'
import {
  ApplyOnStatus,
  MaxShipping,
  NominalLoyalty,
  NominalShipping,
  PercentualLoyalty,
  PercentualShipping,
  RegularBenefit,
  RegularBenefitVariant,
  RegularFreeShipping,
  RegularScope,
  RegularScopeApplyTo,
  ShippingApplyTo,
  type RegularEffect,
  type RegularPromotion,
} from '../../../../typings'
import {
  toAPIAffiliates,
  toAPIBin,
  toAPICluster,
  toAPIFirstPurchase,
  toApiHistoricalPurchaseValue,
  toAPIInstallments,
  toAPIItemPriceRange,
  toAPIListPrice,
  toAPIMarketingTags,
  toAPIOrderValue,
  toAPIPaymentMethods,
  toAPIShippingLocation,
  toAPIShippingMethods,
  toAPISubscription,
  toAPIUtm,
  toAPIUTMICampaign,
} from '../conditions'
import { toAPIGift } from '../gift'
import { APIRestrictionsPayload, toAPIRestrictions } from '../restrictions'

export const REGULAR_INCOMPATIBLE_FIELDS: (keyof RnbCalculatorPayload)[] = [
  // Discount section
  'absoluteShippingDiscountValue',
  'nominalDiscountType',
  'rebatePercentualDiscountValue',
  'percentualTax',
  'shippingPercentualTax',
  'taxExpression',
  'percentualDiscountValueList1',
  'percentualDiscountValueList2',
  'nominalTax',
  'percentualDiscountValueList',

  // Regular section
  'collections1BuyTogether',
  'collections2BuyTogether',
  'minimumQuantityBuyTogether',
  'quantityToAffectBuyTogether',
  'enableBuyTogetherPerSku',
  'listSku1BuyTogether',
  'listSku2BuyTogether',
]

const isFreeShipping = (
  benefit: RegularBenefit
): benefit is RegularFreeShipping => {
  return benefit.type === RegularBenefitVariant.FreeShipping
}

type APIRegularBenefitPayload = Pick<
  Partial<RnbCalculatorPayload>,
  | 'nominalDiscountValue'
  | 'percentualDiscountValue'
  | 'nominalShippingDiscountValue'
  | 'percentualShippingDiscountValue'
  | 'absoluteShippingDiscountValue'
  | 'applyToAllShippings'
  | 'isSlaSelected'
  | 'slasIds'
  | 'nominalRewardValue'
  | 'orderStatusRewardValue'
  | 'skusGift'
  | 'activateGiftsMultiplier'
  | 'percentualRewardValue'
  | 'discountExpression'
  | 'priceTableName'
  | 'maximumUnitPriceDiscount'
>

type APIRegularScopePayload = Pick<
  RnbCalculatorPayload,
  | 'categories'
  | 'categoriesAreInclusive'
  | 'brands'
  | 'brandsAreInclusive'
  | 'products'
  | 'productsAreInclusive'
  | 'skus'
  | 'skusAreInclusive'
  | 'collections'
  | 'collectionsIsInclusive'
>

export function toAPIShipping(
  benefit:
    | NominalShipping
    | MaxShipping
    | PercentualShipping
    | RegularFreeShipping
): Pick<
  APIRegularBenefitPayload,
  | 'nominalShippingDiscountValue'
  | 'percentualShippingDiscountValue'
  | 'absoluteShippingDiscountValue'
  | 'applyToAllShippings'
  | 'isSlaSelected'
  | 'slasIds'
> {
  const KEYS = {
    [RegularBenefitVariant.NominalShipping]: 'nominalShippingDiscountValue',
    [RegularBenefitVariant.PercentualShipping]:
      'percentualShippingDiscountValue',
    [RegularBenefitVariant.FreeShipping]: 'percentualShippingDiscountValue',
    [RegularBenefitVariant.MaxShipping]: 'absoluteShippingDiscountValue',
  }

  const base = {
    [KEYS[benefit.type]]: isFreeShipping(benefit) ? 100 : benefit.value,
  }

  switch (benefit.applyTo) {
    case ShippingApplyTo.all:
      return {
        ...base,
        applyToAllShippings: true,
      }
    case ShippingApplyTo.cheapest:
      return {
        ...base,
        applyToAllShippings: false,
      }
    case ShippingApplyTo.selected:
      return {
        ...base,
        isSlaSelected: false,
        slasIds: benefit.selectedMethods.map((method) => method.id),
      }
  }
}

export function toAPILoyalty(
  benefit: NominalLoyalty | PercentualLoyalty
): Partial<
  Pick<
    APIRegularBenefitPayload,
    'nominalRewardValue' | 'orderStatusRewardValue' | 'percentualRewardValue'
  >
> {
  const mapRewardStatus: Record<
    ApplyOnStatus,
    RnbCalculatorPayload['orderStatusRewardValue']
  > = {
    [ApplyOnStatus.PaymentApproved]: 'payment-approved',
    [ApplyOnStatus.Invoiced]: 'invoiced',
  }

  const mapRewardKey = {
    [RegularBenefitVariant.NominalLoyalty]: 'nominalRewardValue',
    [RegularBenefitVariant.PercentualLoyalty]: 'percentualRewardValue',
  }

  return {
    [mapRewardKey[benefit.type]]: benefit.value,
    orderStatusRewardValue: mapRewardStatus[benefit.status],
  }
}

export function toAPIBenefit(
  benefit: RegularBenefit
): APIRegularBenefitPayload {
  const emptyPayload: APIRegularBenefitPayload = {
    nominalDiscountValue: 0,
    percentualDiscountValue: 0,
    nominalShippingDiscountValue: 0,
    percentualShippingDiscountValue: 0,
    absoluteShippingDiscountValue: 0,
    applyToAllShippings: false,
    nominalRewardValue: 0,
    percentualRewardValue: 0,
    orderStatusRewardValue: undefined,
    skusGift: undefined,
    activateGiftsMultiplier: false,
    discountExpression: undefined,
    priceTableName: undefined,
    maximumUnitPriceDiscount: 0,
  }

  switch (benefit.type) {
    case RegularBenefitVariant.Nominal:
      return {
        ...emptyPayload,
        nominalDiscountValue: benefit.value,
      }
    case RegularBenefitVariant.NominalShipping:
    case RegularBenefitVariant.PercentualShipping:
    case RegularBenefitVariant.FreeShipping:
    case RegularBenefitVariant.MaxShipping: {
      return {
        ...emptyPayload,
        ...toAPIShipping(benefit),
      }
    }
    case RegularBenefitVariant.Percentual: {
      return {
        ...emptyPayload,
        percentualDiscountValue: benefit.value,
      }
    }
    case RegularBenefitVariant.MaxPrice:
      return {
        ...emptyPayload,
        maximumUnitPriceDiscount: benefit.value,
      }

    case RegularBenefitVariant.NominalLoyalty:
    case RegularBenefitVariant.PercentualLoyalty:
      return { ...emptyPayload, ...toAPILoyalty(benefit) }

    case RegularBenefitVariant.Formula:
      return {
        ...emptyPayload,
        discountExpression: benefit.expression,
      }

    case RegularBenefitVariant.Gift:
      return { ...emptyPayload, ...toAPIGift(benefit) }

    case RegularBenefitVariant.PriceTable:
      return {
        ...emptyPayload,
        priceTableName: benefit.value,
      }

    default:
      throw new Error(
        'Cannot convert Promotion from API Payload: Invalid discount type variant'
      )
  }
}

function isInclusive(scopeRestriction: ItemRestriction | undefined): boolean {
  if (!scopeRestriction) return true

  return scopeRestriction.operator === OPERATORS.EQUALS
}

function mapScopeItems(scopeRestriction: ItemRestriction | undefined): Item[] {
  if (!scopeRestriction) return []

  return scopeRestriction.items
}

export function toAPIScope(trigger: RegularScope): APIRegularScopePayload {
  const { applyTo, brands, categories, collections, products, skus } = trigger

  if (applyTo === RegularScopeApplyTo.allItems) {
    return {
      categories: [],
      categoriesAreInclusive: true,
      brands: [],
      brandsAreInclusive: true,
      products: [],
      productsAreInclusive: true,
      skus: [],
      skusAreInclusive: true,
      collections: [],
      collectionsIsInclusive: true,
    }
  }

  return {
    categories: mapScopeItems(categories),
    categoriesAreInclusive: isInclusive(categories),
    brands: mapScopeItems(brands),
    brandsAreInclusive: isInclusive(brands),
    products: mapScopeItems(products),
    productsAreInclusive: isInclusive(products),
    skus: mapScopeItems(skus),
    skusAreInclusive: isInclusive(skus),
    collections: mapScopeItems(collections),
    collectionsIsInclusive: isInclusive(collections),
  }
}

export function toAPIRegularEffect(
  config: RegularEffect
): APIRegularScopePayload & APIRegularBenefitPayload {
  const { scope, benefit } = config
  if (!benefit)
    throw new Error(
      'Cannot convert Promotion to API Payload: Empty Regular Benefit',
      { cause: { payload: config } }
    )

  if (!scope)
    throw new Error(
      'Cannot convert Promotion to API Payload: Empty Regular Scope',
      { cause: { payload: config } }
    )

  return {
    ...toAPIScope(scope),
    ...toAPIBenefit(benefit),
  }
}

export function toAPIConditions(
  conditions: Conditions
): ReturnType<
  typeof toAPIPaymentMethods &
    typeof toAPIBin &
    typeof toAPIOrderValue &
    typeof toAPIUtm &
    typeof toAPICluster &
    typeof toAPIShippingMethods &
    typeof toAPIShippingLocation &
    typeof toAPIListPrice &
    typeof toAPIInstallments &
    typeof toAPIItemPriceRange &
    typeof toAPIAffiliates &
    typeof toApiHistoricalPurchaseValue &
    typeof toAPIMarketingTags &
    typeof toAPIFirstPurchase &
    typeof toAPISubscription &
    typeof toAPIUTMICampaign
> {
  return {
    ...toAPIPaymentMethods(conditions.paymentMethods),
    ...toAPIBin(conditions.bin),
    ...toAPIOrderValue(conditions.orderValue),
    ...toAPIUtm(conditions.utm),
    ...toAPICluster(conditions.cluster),
    ...toAPIShippingMethods(conditions.shippingMethods),
    ...toAPIShippingLocation(conditions.shippingLocation),
    ...toAPIListPrice(conditions.listPrice),
    ...toAPIInstallments(conditions.installments),
    ...toAPIItemPriceRange(conditions.itemPriceRange),
    ...toAPIAffiliates(conditions.affiliates),
    ...toApiHistoricalPurchaseValue(conditions.historicalPurchaseValue),
    ...toAPIMarketingTags(conditions.marketingTags),
    ...toAPIFirstPurchase(conditions.firstPurchase),
    ...toAPISubscription(conditions.subscription),
    ...toAPIUTMICampaign(conditions.utmiCampaign),
  }
}

type APIRegularPayload = ReturnType<
  typeof toAPIOriginalValues &
    typeof toAPIRegularEffect &
    typeof toAPIConditions &
    typeof toAPIRestrictions &
    typeof toAPIPromotionBase
>

export function toAPIRegularRestrictions(
  restrictions: Partial<Restrictions>
): APIRestrictionsPayload {
  const { maxItemsPerAttribute, maxItemsPerCart, ...rest } = restrictions

  if (maxItemsPerAttribute) {
    return toAPIRestrictions({ ...rest, maxItemsPerAttribute })
  }

  return toAPIRestrictions({ ...rest, maxItemsPerCart })
}

export function toAPIRegular(
  data: RegularPromotion,
  originalValues?: RnbCalculatorPayload
): APIRegularPayload {
  return {
    ...toAPIOriginalValues(originalValues, REGULAR_INCOMPATIBLE_FIELDS),
    ...toAPIPromotionBase(data),
    ...toAPIConditions(data.conditions),
    ...toAPIRegularRestrictions(data.restrictions),
    ...toAPIRegularEffect(data.regular),
  }
}
